import * as angular from 'angular';
import * as moment from 'moment';
import * as _ from 'lodash';

import { ComponentDefinition, IValidationViewModel, Group, IFrequency, ValidatedValuesDto, IndexedValue, ValidationScope, RefreshClickData, IValidatedDataVM, DigitalSetSimpleDictionary, Dictionary, AggregationMode, DigitalOrStringConvertibleValue, ActualDataType, IApplicationConfiguration, TagStringValueDto, IValueViewModel } from '../../models';
import { IRootScope } from '../../IRootScope';
import {
    IAuthorizationService,
    ICurrentApplicationService,
    IDateAlignmentHelperService,
    ILogbookService,
    IRelativeGroupHelperService,
    ITranslatedLoggerService,
    IValidatedValuesService,
    IValueFormattingService,
    IvalidationWidgetService
} from '../../services';
import { IColumnValidation } from './IColumnValidation';
import { IValidatedValueSelectionConfiguration } from '../../widgets';
import { formDirtyChanged, guidEmpty, momentFormat, refreshClick, Roles, toDictionary, wideService } from '../../common';
import { ValidatedValueSelectionWidget } from './validatedValueSelection.widget';
import { getTimestampOptions } from '../../common/helpers/timestampDataType';
import { getFreq } from '../../services/helpers/getDefaultFreq';

declare type SourceType = 'manual' | 'measured' | 'calculated' | '';

class ValidatedValueSelectionController implements angular.IComponentController, angular.IOnChanges {
    actualGroup: Group;
    calculatedIdentifiers: IValidationViewModel[];
    childSelecteable: IValidationViewModel[];
    comments: { [guid: string]: string };
    columns: IColumnValidation[];
    hasError?: boolean;
    hasManualInputRole: boolean = false;
    hoverIdentifier: string;
    hoverGroup: SourceType | string;
    identifiers: IValidationViewModel[];
    indexedData: { [identifierGuid: string]: { [groupGuid: string]: { source: string; value: number; formattedValue: string } } };
    innerFrequency: IFrequency;
    isModalLoading: boolean;
    isLoading: boolean;
    manualValues: Dictionary<IndexedValue>;
    measuredValues: Dictionary<IndexedValue>;
    selectableIdentifiers: IValidationViewModel[];
    selectedValues: Dictionary<IndexedValue>;
    styles: { [index: string]: { width: string } };
    tagIdentifiers: IValidationViewModel[];
    validationErrors: { [guid: string]: { hasError: boolean } };

    readonly configuration: IValidatedValueSelectionConfiguration;
    readonly date: Date;
    readonly digitalSets: DigitalSetSimpleDictionary;
    readonly group: Group;
    readonly groupsDictionary: { [guid: string]: Group };
    readonly isDisabled: boolean;
    readonly item: ValidatedValueSelectionWidget;
    readonly referenceGroup?: string;
    readonly stringValues: TagStringValueDto[];
    readonly viewModels: Dictionary<IValueViewModel>;

    private _canceller: angular.IDeferred<{}>;
    private _config: IApplicationConfiguration;

    static $inject = [
        '$q',
        '$scope',
        '$rootScope',
        '$timeout',
        'validatedValuesService',
        'translatedLoggerService',
        'validationWidgetService',
        'relativeGroupHelperService',
        'authorizationService',
        'logbookService',
        'dateAlignmentHelperService',
        'valueFormattingService',
        'currentApplicationService'
    ];

    constructor(
        private _$q: angular.IQService,
        private _$scope: angular.IScope,
        private _$rootScope: IRootScope,
        private _$timeout: angular.ITimeoutService,
        private _validatedValuesService: IValidatedValuesService,
        private _loggerService: ITranslatedLoggerService,
        private _validationWidgetService: IvalidationWidgetService,
        private _relativeGroupHelperService: IRelativeGroupHelperService,
        private _authorizationService: IAuthorizationService,
        private _logbookService: ILogbookService,
        private _dateAlignmentHelperService: IDateAlignmentHelperService,
        private _valueFormattingSerice: IValueFormattingService,
        private _currentApplicationService: ICurrentApplicationService,
    ) {
        this._config = this._currentApplicationService.getApplicationConfiguration();
    }

    $onInit() {
        this.hasManualInputRole = this._authorizationService.isInRoles(Roles.manualInputRoles);

        if (this.group?.validationScope === ValidationScope.MultiplePeriod) {
            this._loggerService.logError('Validation widgets are not compatible with multi-period validation page');
        }

        this._$timeout(() => this.initWatchers());

        this._$scope.$on(refreshClick, (event, data: RefreshClickData) => {
            // only refresh on force, like hit the fresh button
            // not date navigation which is handled by date param changes
            if (data?.force) {
                this.refreshData(data?.force ?? false);
            }
        });

        this.item.ctrl = {
            save: () => this.saveData(),
            cancel: () => this.restoreData(),
            isDirty: false
        };

        this._$scope.$watch(() => this.item.ctrl.isDirty, newValue => {
            this._$rootScope.$broadcast(formDirtyChanged, newValue);
        });
    }

    $onChanges(onChangesObj: angular.IOnChangesObject): void {
        if (onChangesObj['referenceGroup'] || onChangesObj['group']) {
            this.buildStructure();
        }

        // refresh on first init or when date has changed
        if (onChangesObj['date']?.currentValue) {
            const dc = onChangesObj['date'];
            if (!isNaN(dc.currentValue) && (dc.isFirstChange() || dc.currentValue.toISOString() !== dc.previousValue.toISOString())) {
                this.refreshData(false);
            }
        }
    }

    private initWatchers() {
        if (this.configuration) {
            this._$scope.$watch(() => this.configuration.group, (newValue, oldValue) => {
                if (this.configuration) {
                    if (newValue !== oldValue) {
                        this.buildStructure();
                    }
                    if (this.configuration.showShortComment === undefined) {
                        this.configuration.showShortComment = false;
                    }
                    if (this.configuration.showTagName === undefined) {
                        this.configuration.showTagName = true;
                    }
                    if (this.configuration.showUnit === undefined) {
                        this.configuration.showUnit = true;
                    }
                }
            }, true);
            this._$scope.$watchGroup([
                () => this.configuration.hideComment,
                () => this.configuration.showShortComment,
                () => this.configuration.showTagName,
                () => this.configuration.showUnit
            ], () => {
                this.styles = this._validationWidgetService
                    .getStyles(this.configuration, this.tagIdentifiers, this.calculatedIdentifiers, this.columns);
            });

            this._$scope.$watch(
                () => this.configuration.decimals,
                () => this.formatValues()
            );
        }

        this._$scope.$watch(
            () => this.item.name,
            () => this.assignDateTranslation()
        );
    }

    select(identifier: IValidationViewModel, source: SourceType): void {
        if (this.configuration.isColumnMode) {
            let dirty = false;
            this.identifiers
                .forEach(i => {
                    const data = this.getData(i, source);
                    const hasChanged = this.selectOne(i, data);
                    dirty = dirty || hasChanged;
                });
            this.item.ctrl.isDirty = this.item.ctrl.isDirty || dirty;
        } else {
            const data = this.getData(identifier, source);
            this.item.ctrl.isDirty = this.selectOne(identifier, data) || this.item.ctrl.isDirty;
        }
        this.validationErrors = this.errors(this.measuredValues, this.selectedValues, this.indexedData);
        this.hasError = _.some(_.values(this.validationErrors), (e: { hasError: boolean }) => e.hasError);
    }

    selectManual(identifier: IValidationViewModel) {
        this.select(identifier, 'manual');
    }

    inputManual(identifier: IValidationViewModel, data: IndexedValue, value?: number): void {
        this._$timeout(() => {
            this.select(identifier, data && data.value === null ? '' : 'manual');
            this.formatValues();
            this.item.ctrl.isDirty = true;
            this.item.ctrl.editingManual = false;
        });
    }

    inputComment(): void {
        this.item.ctrl.isDirty = true;
    }

    onKeyDown($event: JQuery.Event) {
        const key = $event.keyCode || $event.which;
        const forbidden = [13, 37, 38, 39, 40, 9]; //enter, left, up, right, down, tab
        if (forbidden.indexOf(key) < 0) {
            this.item.ctrl.editingManual = true;
        }
    }

    getClassed(source: SourceType | string, identifier: IValidationViewModel, value: { source: SourceType | string }): {
        [index: string]: boolean;
    } {
        return {
            'hover': this.hoverIdentifier === identifier.guid && this.hoverGroup === source,
            'selected': this.isSelected(source, identifier, value),
            'disable': this.isDisabled
        };
    }

    isSelected(source: SourceType | string, identifier: IValidationViewModel, value: { source: SourceType | string }) {
        const isSelected = this.selectedValues
            && this.selectedValues[identifier.guid] !== undefined
            && value
            && value.source
            && (this.selectedValues[identifier.guid].source === value.source
                || this.selectedValues[identifier.guid].source === source);
        return isSelected;
    }

    isManualSelected(identifier: IValidationViewModel): boolean {
        const isManualSelected = identifier
            && this.selectedValues
            && this.selectedValues[identifier.guid] !== undefined
            && (this.selectedValues[identifier.guid] === this.manualValues[identifier.guid]
                || this.selectedValues[identifier.guid].source === 'manual');
        return isManualSelected;
    }

    getTitle(identifier: IValidationViewModel): string {
        const selectedValue = this.selectedValues && this.selectedValues.hasOwnProperty(identifier.guid)
            ? this.selectedValues[identifier.guid]
            : undefined;

        return selectedValue && selectedValue.user
            ? (selectedValue.validationDate
                ? selectedValue.user === wideService
                    ? 'Preset by _userName_ on _validationDate_'
                    : 'Validated by _userName_ on _validationDate_'
                : 'Default Selection')
            : '';
    }

    clickCell(guid: string, value: IndexedValue, $event: angular.IAngularEvent, edit: boolean = false): void {
        $event.stopPropagation();
        if (!this.item.ctrl.isDirty) {
            const beginDate = moment(this.date);
            const freq = getFreq(this._config, this.innerFrequency);
            this._dateAlignmentHelperService
                .getNextForAggregation(
                    beginDate.clone().add(1, 's'),
                    freq.interval,
                    freq.intervalTimeUnit
                )
                .then(endDate => {
                    this._logbookService.openModal(
                        guid,
                        value.badge,
                        edit,
                        this.hasManualInputRole,
                        this.configuration.showLogbook,
                        { startDate: beginDate.toISOString(), endDate: endDate.toISOString() }
                    );
                });
        }
    }

    private getData(identifier: IValidationViewModel, source: SourceType): IndexedValue {
        const data = (source === 'manual')
            ? this.getManualValue(identifier)
            : (source === 'measured' || source === 'calculated')
                ? this.measuredValues[identifier.guid]
                : this.indexedData[identifier.guid][source];

        return (data !== undefined)
            ? data
            : { value: null, source: source, formattedValue: '-' };
    }

    private getManualValue(identifier: IValidationViewModel): IndexedValue {
        if (this.manualValues && this.manualValues[identifier.guid]) {
            const val = this.manualValues[identifier.guid];
            const result = val
                ? { value: val.value, source: 'manual', formattedValue: String(val.value) }
                : val;
            this.formatIndexedValue(result, identifier);
            return result;
        }
    }

    private selectOne(identifier: IValidationViewModel, data: IndexedValue): boolean {
        const old = this.selectedValues[identifier.guid];
        if (old?.badge && data) {
            data.badge = angular.copy(old.badge);
        }
        this.selectedValues[identifier.guid] = data;
        return !old || data
            && ((data.source === undefined || old.source !== data.source)
                && (old.value !== data.value));
    }

    private saveData(): angular.IPromise<void> {
        this.isLoading = true;

        const toSave: ValidatedValuesDto[] = [];
        if (this.selectedValues) {
            Object.keys(this.selectedValues)
                .forEach(key => {
                    const manualValue = this.manualValues[key] ? this.manualValues[key].value : undefined;
                    const vm = this.identifiers.find(i => i.guid === key);
                    const value = this.selectedValues[key];
                    const source = value.source === 'manual'
                        ? (manualValue !== undefined && manualValue !== null
                            ? guidEmpty
                            : vm?.selectableIdentifiers[0]?.guid)
                        : value.source;
                    if (value.value !== undefined) {
                        toSave.push({
                            guid: key,
                            timestamp: this.date.toISOString(),
                            source: source,
                            value: value.value,
                            manualValue: manualValue,
                            comment: this.comments[key],
                            user: value.user,
                            validationDate: moment(value.validationDate).format(momentFormat)
                        });
                    }
                });
        }

        const promise = this.configuration.launchTask
            ? this.asyncSave(toSave)
            : this.syncSave(toSave);

        return promise.finally(() => {
            this.item.ctrl.form?.$setPristine();
            this.isLoading = false;
        });
    }

    private syncSave(toSave: ValidatedValuesDto[]): angular.IPromise<void> {
        const defer = this._$q.defer<void>();
        this._canceller = this._$q.defer<Object>();
        this.isLoading = true;

        // Don't use modal for now
        // const modal = $(`#reprocessModal${this.actualGroup.guid}`);
        // this.isModalLoading = true;
        // modal.on('hidden.bs.modal', (e) => {
        //     $(e.currentTarget).unbind('hidden.bs.modal');
        //     self.cancelSyncSave();
        //     defer.resolve();
        // });
        // modal.modal({ backdrop: 'static', keyboard: true, show: true });

        this._validatedValuesService
            .save(toSave, true, this.item.configuration.taskOptionsOverride, this._canceller.promise)
            .then(() => this.item.ctrl.isDirty = false)
            .catch(reason => this._loggerService.logWarning(`Reprocess aborted`, true))
            .finally(() => {
                defer.resolve();
                // modal.modal('hide');
            });

        return defer.promise;
    }

    private asyncSave(toSave: ValidatedValuesDto[]): angular.IPromise<void> {
        return this._validatedValuesService
            .save(toSave, false, this.item.configuration.taskOptionsOverride)
            .then(count => {
                if (count > 0) {
                    this._loggerService.logSuccess(`${count} new reprocessing tasks launched.`, true);
                }
                this._loggerService.logSuccess('Saved');
                this.item.ctrl.isDirty = false;
                return this.refreshData(true);
            })
            .catch(reason => {
                this._loggerService.logError('Save failed', true, reason && reason.message ? reason.message : reason);
            });
    }

    private restoreData() {
        this.resetInvalidForm();
        this.refreshData();
    }

    private resetInvalidForm() {
        const form: angular.IFormController = this.item.ctrl.form;

        if (this.indexedData) {
            Object.keys(this.indexedData)
                .forEach(k => {
                    const control = form[k] as angular.INgModelController;
                    if (control && control.$invalid) {
                        control.$setViewValue(undefined);
                        control.$setValidity('float', true);
                        control.$render();
                    }
                });
            form.$setPristine();
            form.$setUntouched();
        }
    }

    private buildStructure() {
        this.getActualGroup()
            .then(actual => {
                this.actualGroup = actual;
                this.refreshStructure();
            });
    }

    private refreshStructure() {
        if (this.actualGroup && this.configuration) {
            const cfg = this._validationWidgetService
                .getWidgetConfiguration(this.actualGroup, this.configuration, this.digitalSets, this.viewModels);
            this.identifiers = cfg.identifiers;
            this.selectableIdentifiers = cfg.selectableIdentifiers;
            this.childSelecteable = cfg.childSelecteable;
            this.columns = cfg.columns;
            this.tagIdentifiers = cfg.tagIdentifiers;
            this.calculatedIdentifiers = cfg.calculatedIdentifiers;
            this.styles = cfg.styles;
            this.refreshData(false);
        }
    }

    private refreshData(force?: boolean): angular.IPromise<void> {
        if (this.date) {
            //columns: children identifiers that can be selected
            //tagIdentifiers: identifiers that can be validated against a tag
            this.assignDateTranslation();
            if (_.some(this.columns) || _.some(this.tagIdentifiers) || _.some(this.calculatedIdentifiers) || _.some(this.selectableIdentifiers)) {
                this.isLoading = true;
                return this._validatedValuesService
                    .readAtOnce(
                        this.date.toISOString(),
                        this.identifiers,
                        this.tagIdentifiers.concat(this.calculatedIdentifiers),
                        this.childSelecteable,
                        this.selectableIdentifiers,
                        this.innerFrequency,
                        this.item.configuration.showQuality,
                        this.item.configuration.showLogbook,
                        force,
                    )
                    .then(data => this.mapData(data))
                    .finally(() => {
                        this.isLoading = false;
                        this.item.ctrl.isDirty = false;
                    });
            }
        }
    }

    private mapData(data: IValidatedDataVM) {
        this.selectedValues = Object.assign({}, data.selectedValues);
        this.identifiers
            .filter(identifier => identifier.actualDataType === ActualDataType.Timestamp)
            .forEach(identifier => {
                const key = identifier.guid;
                let d = data.manualValues[key];
                if (!d) {
                    // Init empty object for first use
                    d = {
                        formattedValue: '-',
                        source: 'manual',
                        value: null
                    };
                    data.manualValues[key] = d;
                }
                const onApply = value => {
                    try {
                        d.value = value;
                        this.inputManual(identifier, d);
                    } catch (e) {
                        console.error(e);
                    }
                };
                Object.assign(d, getTimestampOptions(d.value, onApply));
            });
        this.manualValues = Object.assign({}, data.manualValues);
        this.measuredValues = Object.assign({}, data.measuredValues);
        this.comments = Object.assign({}, data.comments);
        this.indexedData = Object.assign({}, data.loadingData);
        this.validationErrors = this.errors(this.measuredValues, this.selectedValues, this.indexedData);
        this.hasError = _.some(_.values(this.validationErrors), (e: { hasError: boolean }) => e.hasError);
        this.formatValues();
    }

    private errors(measuredValues: Dictionary<IndexedValue>, selectedValues: Dictionary<IndexedValue>, indexedData: { [guid: string]: Dictionary<IndexedValue> }) {
        const result = {};
        this.identifiers
            .forEach(i => {
                let value = measuredValues[i.guid];
                const selectedValue = selectedValues[i.guid];
                const isManual = this.isManualSelected(i);
                let hasError = false;
                if (!isManual) {
                    const source: SourceType = 'measured';
                    hasError = this.getError(source, value, selectedValue, i);

                    if (!hasError && this.columns && this.columns.length) {
                        const indexed = indexedData[i.guid];
                        this.columns.forEach(c => {
                            value = indexed[c.guid];
                            if (value && selectedValue && value.source === selectedValue.source) {
                                hasError = this.getError(c.guid, value, selectedValue, i);
                            }
                        });
                    }
                }
                result[i.guid] = {
                    hasError: hasError
                };
            });
        return result;
    }

    private getError(
        source: string,
        measuredValue: { source: string; value: number },
        selectedValue: { source: string; value: number },
        vm: IValidationViewModel
    ) {
        const isSelected = this.isSelected(source, vm, measuredValue);
        const hasValue = measuredValue
            && measuredValue.value !== null
            && measuredValue.value !== undefined
            && !isNaN(measuredValue.value);
        const isSameValue = !!selectedValue && hasValue && Math.abs((measuredValue.value - selectedValue.value)
            / (measuredValue.value ? measuredValue.value : 1)) < 1e-6;
        const hasError = !!(hasValue && (!isSelected || !isSameValue));
        return hasError;
    }

    private assignDateTranslation() {
        if (this.item) {
            if (this.actualGroup && this.group?.guid !== this.actualGroup?.guid) {
                this.item.displayName = this.item.name + ` (${this.actualGroup.uniqueName})`;
            }
        }
    }

    private getActualGroup(): angular.IPromise<Group> {
        let configuration = this.configuration;
        if (this.referenceGroup) {
            configuration = Object.assign({}, configuration, { referenceGroup: this.referenceGroup });
        }
        if (!configuration.referenceGroup) {
            return this._$q.when(this.group);
        }
        return this._$q.when(this._relativeGroupHelperService.getGroup(configuration, this.group, this.groupsDictionary));
    }

    private formatValues(): void {
        const index = toDictionary(this.identifiers);
        this.formatIndexValueSource(this.selectedValues, index);
        this.formatIndexValueSource(this.manualValues, index);
        this.formatIndexValueSource(this.measuredValues, index);
        if (this.indexedData) {
            Object.keys(this.indexedData).forEach(guid => {
                const vm = index[guid];
                const current = this.indexedData[guid];
                Object.keys(current).forEach(g => this.formatIndexedValue(current[g], vm));
            });
        }
    }

    private formatIndexValueSource(src: Dictionary<IndexedValue>, vms: Dictionary<IValidationViewModel>): void {
        if (src) {
            Object.keys(src).forEach(k => this.formatIndexedValue(src[k], vms[k]));
        }
    }

    private formatIndexedValue(val: IndexedValue, vm: IValidationViewModel) {
        const convertible: DigitalOrStringConvertibleValue = {
            value: val.value,
            valueType: vm.valueType
        };
        val.formattedValue = this._valueFormattingSerice.formatValue(convertible, vm, this.digitalSets, AggregationMode.None, this.configuration.decimals, undefined, this.stringValues);
    }
}

export const wtValidatedValueSelection: ComponentDefinition = {
    componentId: 'wtValidatedValueSelection',
    controller: ValidatedValueSelectionController,
    template: require('./validatedValueSelection.component.html'),
    controllerAs: 'vm',
    bindings: {
        configuration: '<',
        date: '<',
        digitalSets: '<',
        group: '<',
        groupsDictionary: '<',
        hasError: '=?',
        innerFrequency: '<?',
        isDisabled: '<',
        item: '<',
        referenceGroup: '<?',
        stringValues: '<',
        viewModels: '<'
    }
};